import {
    type ColumnsSettings,
    getSortDirShort,
    mapColumnsSettingsToStrings,
    parseColumnsSettingsStrings,
    parseSorting,
} from '@rio-cloud/rio-uikit/routeUtils';
import compact from 'lodash/fp/compact';
import isArray from 'lodash/fp/isArray';
import isEmpty from 'lodash/fp/isEmpty';
import isEqual from 'lodash/fp/isEqual';
import isNil from 'lodash/fp/isNil';
import qs, { type IParseBaseOptions, type IStringifyOptions } from 'qs';

import { getMapLayersByIds, getMapLayersIds, getMapTypeById, getMapTypeId, mapTypeSet } from '../data/dataDefinition';
import type { UrlState } from '../features/app/appReducers/appReducer';
import type { ActiveSidebarElement } from '../features/app/appReducers/dataReducer';
import { defaultMapState } from '../features/map/mapSlice';
import { assetTabs, getAssetTabById, getAssetTabId } from '../features/sidebar/sidebarSlice';
import { TABLE_COLUMN_ASSET_GROUP, TABLE_COLUMN_INSIDE_GEOFENCES, tableConfig } from '../features/table/tableConfig';
import { ASSET_DISPLAY_NAME_VEHICLE_NAME, TREE_CATEGORY_POIS, TREE_CATEGORY_ROUTE } from '../features/tree/treeSlice';
import type { CoordinatesShort } from '../services/types';
import { getBasePath, LIST_PATH, MAP_PATH } from './routes';

const qsStringifyOptions: IStringifyOptions = {
    arrayFormat: 'comma', // required to stringify arrays into comma separated strings
    indices: false, // don't use array indices
    encode: false, // don't encode the entire string as it will be done individually for certain params
    skipNulls: true, // required to remove empty params
};

const qsParseOption: IParseBaseOptions = {
    comma: true, // required to parse comma separated string into array
};

export const isMapView = () => isEqual(getBasePath(), MAP_PATH);

const getValueAsArray = (value: string[] | string) => (isArray(value) ? value : [value]);
const getArrayOrNull = <T>(array: T[]) => (isEmpty(compact(array)) ? null : array);
const getAsArrayOrNull = (value: string[] | string | undefined): string[] | null =>
    !value || isEmpty(value) ? null : getValueAsArray(value);

const pathRegExp = new RegExp(`(${LIST_PATH})|(${MAP_PATH})`);

const getAssetId = (pathname = '') => pathname.replace(pathRegExp, '').replace('/', '') || '';

const isValidAssetId = (assetId: string) =>
    assetId ? assetId.match('^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$') : false;

const getValidAssetId = (pathname = '') => {
    const assetId = getAssetId(pathname);
    return isValidAssetId(assetId) ? assetId : undefined;
};

const getActiveAssetIdFragment = (activeAssetId: string) => (activeAssetId ? `/${activeAssetId}` : '');

const sanitizeSearchValue = (value: string | undefined) => (value ? value.replace('%', '%25') : '');

const getValueOrNull = <T>(value: T, defaultValue: T) => (value === defaultValue ? null : value);

const getNumber = (value: unknown) => Number(value);

const getCenter = (lat: string | undefined, lng: string | undefined) => {
    if (lat && lng) {
        return {
            lat: getNumber(lat),
            lng: getNumber(lng),
        };
    }
    return;
};

const encodeTreeState = (
    isTreeOpen: boolean,
    treeCategory: string,
    treePoiCategory: string,
    treeRoutePlanningCategory: string
) => {
    if (!treeCategory) {
        return null;
    }

    const prefix = isTreeOpen ? '' : '-';

    // Add "treePoiCategory" only for pois - &tree=pois;geofences
    // Note, the reason why this is a combined param instead of two separate is
    // to easily set the poi tab with one single command via widget action
    const poiState = treeCategory === TREE_CATEGORY_POIS && treePoiCategory ? `;${treePoiCategory}` : '';

    // Add subcategory for routePlanning
    const routePlanningState =
        treeCategory === TREE_CATEGORY_ROUTE && treeRoutePlanningCategory ? `;${treeRoutePlanningCategory}` : '';

    const subCategory = poiState || routePlanningState;

    return `${prefix}${treeCategory}${subCategory}`;
};

// tree=route;geo
// tree=poi;charging
const parseTreeState = (value: string | undefined) => ({
    treeCategory: value ? value.replace('-', '').split(';')[0] : 'assets',
    isTreeOpen: value ? !value.startsWith('-') : true,
    treePoiCategory: value?.startsWith('poi') ? value.split(';')[1] : undefined,
    treeRoutePlanningCategory: value?.startsWith('route') ? value.split(';')[1] : undefined,
});

const isBooleanOrNull = (value = '') => {
    const lowerCaseValue = value.toLowerCase();
    if (lowerCaseValue === 'true') {
        return true;
    }
    if (lowerCaseValue === 'false') {
        return false;
    }
    return null;
};

const getTrueOrNull = (value: boolean) => value || null;
const getFalseOrNull = (value: boolean) => !value || null;

const DEFAULT_TAB_ID = '1';
const DEFAULT_MAP_TYPE = '0';
const DEFAULT_TREE_CATEGORY = 'assets';

type ParseRouteProps = {
    location: {
        search?: string;
        pathname?: string;
    };
};

type ParsedSearchParams = Record<string, string | string[] | undefined>;

export const parseRoute = (router: ParseRouteProps | undefined): UrlState => {
    const location = router?.location;
    const searchParams = location?.search ? location?.search.replace('?', '') : '';
    const {
        sort,
        tq,
        chunks,
        viewType,
        columns,
        showTableSettings,
        activityFilter,
        assetFilter,
        notificationFilter,
        tabId,
        assetIds,
        groupIds,
        driverIds,
        lock,
        lat,
        lng,
        z,
        tree,
        eg,
        ag,
        dg,
        ft,
        mapType,
        cluster,
        wpois,
        cpois,
        bpois,
        gf,
        ml,
        cs,
        info,
        infoChapter,
        positionSharingDialog,
        dn,
        poiId,
        geofenceId,
        cPoiId,
        sPoiId,
    } = qs.parse(searchParams, qsParseOption) as ParsedSearchParams;

    const sorting = sort && parseSorting(sort as string);

    // Workaround: the qs library parses "%25" automatically to "%" even though we disabled the decoding.
    // Since this would fail when using decodeURIComponent() we need to replace the "%" back to the encoded value.
    const fixedTableSearchValue = sanitizeSearchValue(tq as string);

    const center = getCenter(lat as string, lng as string);

    const isShowCluster = isBooleanOrNull(cluster as string);

    const { treeCategory, isTreeOpen, treePoiCategory, treeRoutePlanningCategory } = parseTreeState(tree as string);

    const assetDisplayNameType = dn as string;

    const getActiveSidebarElement = (searchParams: ParsedSearchParams): ActiveSidebarElement | undefined => {
        const assetId = getValidAssetId(location?.pathname);
        if (assetId) {
            return { type: 'asset', id: assetId };
        }
        if (searchParams.poiId && typeof searchParams.poiId === 'string') {
            return { type: 'poi', id: searchParams.poiId };
        }
        if (searchParams.geofenceId && typeof searchParams.geofenceId === 'string') {
            return { type: 'geofence', id: searchParams.geofenceId };
        }
        if (searchParams.cPoiId && typeof searchParams.cPoiId === 'string') {
            return { type: 'chargingStation', id: searchParams.cPoiId };
        }
        if (searchParams.sPoiId && typeof searchParams.sPoiId === 'string') {
            return { type: 'simplePayPoi', id: searchParams.sPoiId };
        }
        return;
    };

    const { sortBy, sortDir } =
        typeof sorting === 'object' && sorting !== null
            ? {
                  sortBy: sorting?.sortBy,
                  sortDir: sorting?.sortDir,
              }
            : {};

    // For migration only when adding new columns to the table config
    if (columns && Array.isArray(columns)) {
        if (!columns.includes(TABLE_COLUMN_ASSET_GROUP) && !columns.includes(`-${TABLE_COLUMN_ASSET_GROUP}`)) {
            columns.push(`-${TABLE_COLUMN_ASSET_GROUP}`);
        }
        if (
            !columns.includes(TABLE_COLUMN_INSIDE_GEOFENCES) &&
            !columns.includes(`-${TABLE_COLUMN_INSIDE_GEOFENCES}`)
        ) {
            columns.push(`-${TABLE_COLUMN_INSIDE_GEOFENCES}`);
        }
    }

    return {
        search: fixedTableSearchValue && decodeURIComponent(fixedTableSearchValue),
        sortBy,
        sortDir,
        chunks: chunks && Number.parseInt(chunks as string, 10),
        viewType,
        columns: columns && parseColumnsSettingsStrings(columns as string[], tableConfig.defaultColumnsDetails),
        showTableSettings: isBooleanOrNull(showTableSettings as string),
        activityFilter: getAsArrayOrNull(activityFilter),
        assetFilter: getAsArrayOrNull(assetFilter),
        notificationFilter: getAsArrayOrNull(notificationFilter),
        tabId: getAssetTabById(Number(tabId ?? '1')),
        selectedAssetIds: getAsArrayOrNull(assetIds),
        selectedAssetGroupIds: getAsArrayOrNull(groupIds),
        selectedDriverIds: getAsArrayOrNull(driverIds),
        lockOnAsset: isBooleanOrNull(lock as string),
        center,
        zoom: z ? getNumber(z) : undefined,
        mapType: getMapTypeById(Number(mapType)),
        mapLayers: getMapLayersByIds(getAsArrayOrNull(ml)) ?? [],
        showCluster: isShowCluster === null ? true : isShowCluster,
        showWorkshopPois: isBooleanOrNull(wpois as string),
        showCustomerPois: isBooleanOrNull(cpois as string),
        showSimplePayPois: isBooleanOrNull(bpois as string),
        showGeofences: isBooleanOrNull(gf as string),
        showChargingStations: isBooleanOrNull(cs as string),
        treeCategory,
        treePoiCategory,
        treeRoutePlanningCategory,
        isTreeOpen,
        showEmptyGroups: isBooleanOrNull(eg as string),
        showAssetGroups: isBooleanOrNull(ag as string),
        showDriverGroups: isBooleanOrNull(dg as string),
        showFuelType: isBooleanOrNull(ft as string),
        showServiceInfoDialog: isBooleanOrNull(info as string),
        serviceInfoChapter: infoChapter,
        showShareLinkDialog: isBooleanOrNull(positionSharingDialog as string),
        assetDisplayNameType,
        activeSidebarElement: getActiveSidebarElement({ cPoiId, geofenceId, poiId, sPoiId }),
    };
};

export type MakeRouteProp = {
    sortBy?: string;
    sortDir?: string;
    tableSearch?: string;
    chunks?: number;
    viewType?: string;
    showTableSettings?: boolean;
    columns?: ColumnsSettings;
    activityFilter?: string[];
    assetFilter?: string[];
    notificationFilter?: string[];
    activeAssetId?: string;
    poiId?: string;
    geofenceId?: string;
    cPoiId?: string;
    sPoiId?: string;
    selectedAssetIds?: string[];
    selectedAssetGroupIds?: string[];
    selectedDriverIds?: string[];
    tabId?: string;
    lockOnAsset?: boolean;
    center?: CoordinatesShort;
    zoom?: number;
    mapType?: string;
    mapLayers?: string[];
    showCluster?: boolean;
    showWorkshopPois?: boolean;
    showCustomerPois?: boolean;
    showSimplePayPois?: boolean;
    showGeofences?: boolean;
    showChargingStations?: boolean;
    treeCategory?: string;
    treePoiCategory?: string;
    treeRoutePlanningCategory?: string;
    isTreeOpen?: boolean;
    areEmptyGroupsShown?: boolean;
    areAssetGroupsShown?: boolean;
    areDriverGroupsShown?: boolean;
    isFuelTypeShown?: boolean;
    showServiceInfoDialog?: boolean;
    serviceInfoChapter?: string;
    showShareLinkDialog?: boolean;
    assetDisplayNameType?: string;
};

export const makeRoute = ({
    sortBy,
    sortDir,
    tableSearch,
    chunks,
    viewType,
    showTableSettings,
    columns,
    activityFilter,
    assetFilter,
    notificationFilter,
    activeAssetId,
    selectedAssetIds,
    selectedAssetGroupIds,
    selectedDriverIds,
    tabId,
    lockOnAsset,
    center,
    zoom,
    mapType,
    mapLayers,
    showCluster,
    showWorkshopPois,
    showCustomerPois,
    showSimplePayPois,
    showGeofences,
    showChargingStations,
    treeCategory,
    treePoiCategory,
    treeRoutePlanningCategory,
    isTreeOpen,
    areEmptyGroupsShown,
    areAssetGroupsShown,
    areDriverGroupsShown,
    isFuelTypeShown,
    showServiceInfoDialog,
    serviceInfoChapter,
    showShareLinkDialog,
    assetDisplayNameType,
    geofenceId,
    poiId,
    cPoiId,
    sPoiId,
}: MakeRouteProp) => {
    const mapTypeId = mapType ? getMapTypeId(mapType, mapTypeSet) : null;
    const assetSidebarTabId = tabId ? getAssetTabId(tabId, assetTabs) : null;
    const currentTreeCategory = encodeTreeState(
        isTreeOpen ?? false,
        treeCategory ?? '',
        treePoiCategory ?? '',
        treeRoutePlanningCategory ?? ''
    );

    // In case cluster is not defined, it should be omitted from the URL as it is most likely the table view
    const isClusterDefined = !isNil(showCluster);
    const hasCluster = showCluster ? null : isClusterDefined ? false : null;

    // In case groups are not defined, they should be omitted from the URL as it is most likely the table view
    const showAssetGroups = isNil(areAssetGroupsShown) ? null : getFalseOrNull(areAssetGroupsShown);
    const showDriverGroups = isNil(areDriverGroupsShown) ? null : getFalseOrNull(areDriverGroupsShown);

    // Since showing the fuel type is the default, omit the value unless it is disabled
    const showFuelType = isFuelTypeShown === false ? false : null;

    // Set props to undefined or null in order to remove it from the URL when not defined
    const params = {
        tq: tableSearch ? encodeURIComponent(tableSearch) : null,
        sort: sortBy && sortDir && `${getSortDirShort(sortDir)}${sortBy}`,
        chunks,
        viewType,
        assetIds: selectedAssetIds && getArrayOrNull(selectedAssetIds),
        groupIds: selectedAssetGroupIds && getArrayOrNull(selectedAssetGroupIds),
        driverIds: selectedDriverIds && getArrayOrNull(selectedDriverIds),
        columns: columns && mapColumnsSettingsToStrings(columns),
        showTableSettings: isNil(showTableSettings) ? null : getTrueOrNull(showTableSettings),
        activityFilter: activityFilter && getArrayOrNull(activityFilter),
        assetFilter: assetFilter && getArrayOrNull(assetFilter),
        notificationFilter: notificationFilter && getArrayOrNull(notificationFilter),
        // If sidebar tabId Id is the first tab, we hide it from the URL as it is the default
        tabId: assetSidebarTabId === DEFAULT_TAB_ID ? null : assetSidebarTabId,
        lock: isNil(lockOnAsset) ? null : getValueOrNull(lockOnAsset, defaultMapState.lockOnAsset),
        lat: center && Number(center.lat.toPrecision(15)),
        lng: center && Number(center.lng.toPrecision(15)),
        z: zoom,
        // If mapType Id is the default map type, we hide it from the URL as it is the default
        mapType: mapTypeId === DEFAULT_MAP_TYPE ? null : mapTypeId,
        ml: !mapLayers || isEmpty(mapLayers) ? null : getMapLayersIds(mapLayers),
        // If clusters are disabled we add it to the URL as "false", otherwise we hide it as it is the default
        cluster: hasCluster,
        wpois: isNil(showWorkshopPois) ? null : getTrueOrNull(showWorkshopPois),
        cpois: isNil(showCustomerPois) ? null : getTrueOrNull(showCustomerPois),
        bpois: isNil(showSimplePayPois) ? null : getTrueOrNull(showSimplePayPois),
        gf: isNil(showGeofences) ? null : getTrueOrNull(showGeofences),
        cs: isNil(showChargingStations) ? null : getTrueOrNull(showChargingStations),
        // If tree is on the assets category, we hide it from the URL as it is the default
        tree: currentTreeCategory === DEFAULT_TREE_CATEGORY ? null : currentTreeCategory,
        eg: isNil(areEmptyGroupsShown) ? null : getTrueOrNull(areEmptyGroupsShown),
        ft: showFuelType,
        ag: showAssetGroups,
        dg: showDriverGroups,
        info: isNil(showServiceInfoDialog) ? null : getTrueOrNull(showServiceInfoDialog),
        infoChapter: showServiceInfoDialog ? serviceInfoChapter : null,
        positionSharingDialog: isNil(showShareLinkDialog) ? null : getTrueOrNull(showShareLinkDialog),
        dn: assetDisplayNameType === ASSET_DISPLAY_NAME_VEHICLE_NAME ? null : assetDisplayNameType,
        geofenceId,
        poiId,
        cPoiId,
        sPoiId,
    };

    const queryParams = qs.stringify(params, qsStringifyOptions);
    const searchFragment = queryParams && `?${queryParams}`;

    return `${getBasePath()}${getActiveAssetIdFragment(activeAssetId ?? '')}${searchFragment}`;
};
